/**
 * CourtPicker — reusable GOV.UK "Find a Court or Tribunal" search.
 * Postcode in → nearest courts out (Money claims / Bankruptcy filter),
 * powered by the existing backend court_finder service.
 *
 * Used by: IssuingCourtCard (case file) + AddLegalEventDialog (hearing /
 * petition events). Later: doc-generation wizards for demands.
 */
import { useState } from "react";
import { Loader2, Search, Landmark, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { searchCourtsByPostcode, searchCourtsByName } from "@/lib/api";

const AOL_OPTIONS = [
  { value: "Money claims", label: "Money claims" },
  { value: "Bankruptcy",   label: "Bankruptcy" },
  { value: "_all",         label: "All areas of law" },
];

export function formatCourtAddress(court) {
  const a = court?.address || {};
  return [...(a.lines || []), a.town, a.postcode].filter(Boolean).join(", ");
}

export default function CourtPicker({ onSelect, compact = false }) {
  const [mode, setMode]           = useState("postcode"); // postcode | name (Emma #2)
  const [postcode, setPostcode]   = useState("");
  const [nameQ, setNameQ]         = useState("");
  const [aol, setAol]             = useState("Money claims");
  const [searching, setSearching] = useState(false);
  const [results, setResults]     = useState(null); // null = not searched yet

  const search = async () => {
    setSearching(true);
    try {
      let r;
      if (mode === "name") {
        const q = nameQ.trim();
        if (q.length < 2) { toast.error("Type at least 2 characters of the court name"); setSearching(false); return; }
        r = await searchCourtsByName(q, 10);
      } else {
        const pc = postcode.trim();
        if (!pc) { toast.error("Enter a postcode first"); setSearching(false); return; }
        r = await searchCourtsByPostcode(pc, aol === "_all" ? "" : aol, 5);
      }
      if (r?.success === false) {
        setResults([]);
        toast.error(r.error || "No courts found");
      } else {
        setResults(r.courts || []);
      }
    } catch (e) {
      setResults([]);
      toast.error("Court search failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSearching(false);
    }
  };

  const switchMode = (m) => { setMode(m); setResults(null); };

  return (
    <div className="space-y-2" data-testid="court-picker">
      <div className="flex items-center gap-1 text-[10px]">
        <button type="button" onClick={() => switchMode("postcode")}
          className={`px-2 py-0.5 rounded-full border ${mode === "postcode" ? "bg-slate-800 text-white border-slate-800" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="court-picker-mode-postcode">By postcode</button>
        <button type="button" onClick={() => switchMode("name")}
          className={`px-2 py-0.5 rounded-full border ${mode === "name" ? "bg-slate-800 text-white border-slate-800" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="court-picker-mode-name">By court name</button>
      </div>
      <div className="flex items-center gap-1.5">
        {mode === "name" ? (
          <Input
            placeholder="Court name (e.g. Manchester, Central London)"
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            className="h-8 text-xs flex-1"
            data-testid="court-picker-name"
          />
        ) : (
          <Input
            placeholder="Postcode (e.g. SW1A 1AA)"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && search()}
            className="h-8 text-xs flex-1"
            data-testid="court-picker-postcode"
          />
        )}
        {!compact && mode === "postcode" && (
          <Select value={aol} onValueChange={setAol}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="court-picker-aol">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AOL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" variant="outline" className="h-8" onClick={search} disabled={searching} data-testid="court-picker-search">
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {results !== null && (
        results.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-1">No courts found — {mode === "name" ? "try a shorter name." : "check the postcode."}</div>
        ) : (
          <ul className="divide-y border rounded-md max-h-56 overflow-auto bg-white" data-testid="court-picker-results">
            {results.map((c) => (
              <li key={c.slug || c.name} className="p-2 flex items-start gap-2 hover:bg-slate-50">
                <Landmark className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-800 leading-tight">{c.name}</div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">{formatCourtAddress(c) || "—"}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {c.distance_miles != null && <span>{c.distance_miles} mi</span>}
                    {c.dx_number && <span> · DX {c.dx_number}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px] px-2 shrink-0"
                  onClick={() => onSelect({ ...c, postcode_searched: postcode.trim() })}
                  data-testid={`court-picker-select-${c.slug || "court"}`}
                >
                  Select
                </Button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
